import { cn } from '@/shared/helpers/classnames'

import { forwardRef, useLayoutEffect, useRef } from 'react'

import type { CheckboxProps } from './types'

/**
 * Checkbox component with indeterminate state support.
 *
 * This checkbox extends standard behavior with a third "indeterminate" state
 * useful for hierarchical or partial selections.
 * It automatically applies the appropriate styles based on the state.
 *
 * Features:
 * - Three states: checked, unchecked, indeterminate
 * - Automatic styles based on state
 * - Support for all HTML input attributes
 * - Native indeterminate property handling
 * - Conditional classes for disabled and primary states
 * - Ref forwarding for direct DOM access
 *
 * @example
 * ```tsx
 * // Simple checkbox
 * <Checkbox
 *   checked={isSelected}
 *   onChange={(e) => setIsSelected(e.target.checked)}
 *   disabled={isLoading}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Indeterminate checkbox for partial selection
 * <Checkbox
 *   checked={allSelected}
 *   indeterminate={someSelected && !allSelected}
 *   onChange={handleSelectAll}
 * />
 * ```
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      onChange,
      disabled,
      error,
      indeterminate = false,
      size = 'md',
      readOnly = false,
      ...props
    },
    ref,
  ) => {
    const localRef = useRef(null)
    const resolvedRef = ref || localRef

    useLayoutEffect(() => {
      if (resolvedRef) {
        // @ts-expect-error TS2322: indeterminate does only exist for Checkbox inputs and not in HTMLInputElement
        resolvedRef.current.indeterminate = indeterminate
      }
    }, [indeterminate, resolvedRef])

    return (
      <input
        ref={resolvedRef}
        type="checkbox"
        className={cn('checkbox', {
          'checkbox-sm': size === 'sm',
          'checkbox-md': size === 'md',
          'checkbox-lg': size === 'lg',
          'checkbox-primary': !readOnly && (checked || indeterminate),
          'checkbox-secondary': readOnly,
          'checkbox-disabled': disabled,
          'checkbox-indeterminate': indeterminate,
          'checkbox-checked': checked,
          'checkbox-error': error,
          'checkbox-readonly': readOnly,
        })}
        checked={checked}
        onChange={(readOnly ? undefined : onChange) ?? (() => {})}
        disabled={disabled}
        tabIndex={readOnly ? -1 : undefined}
        aria-checked={indeterminate || checked}
        {...props}
      />
    )
  },
)

Checkbox.displayName = 'Checkbox'
